import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, RefreshCw, AlertCircle, ExternalLink, Send, Wallet, UserPlus, Banknote } from 'lucide-react';

const healthBadge = (status) => {
  if (status === 'connected') return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="paystack-health-badge">CONNECTED (LIVE)</Badge>;
  if (status === 'auth_error') return <Badge className="bg-rose-50 text-rose-700 border border-rose-200" data-testid="paystack-health-badge">AUTH ERROR</Badge>;
  if (status === 'pending_credentials') return <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="paystack-health-badge">AWAITING CREDENTIALS</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 border border-slate-200" data-testid="paystack-health-badge">{(status || 'UNKNOWN').toUpperCase()}</Badge>;
};

const statusChip = (s) => {
  const cls = s === 'transferred' || s === 'paid' ? 'bg-emerald-50 text-emerald-700'
    : s === 'processing' || s === 'initialized' || s === 'otp_required' ? 'bg-sky-50 text-sky-700'
    : s === 'blocked_insufficient_balance' ? 'bg-amber-50 text-amber-700'
    : 'bg-rose-50 text-rose-700';
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${cls}`}>{s}</span>;
};

const PaystackLayer = () => {
  const [config, setConfig] = useState(null);
  const [txns, setTxns] = useState([]);
  const [kcb, setKcb] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [banks, setBanks] = useState([]);
  const [fx, setFx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [recForm, setRecForm] = useState({ rtype: 'mobile_money', name: '', account_number: '', bank_code: '' });
  const [settleForm, setSettleForm] = useState({ usd_amount: 100, recipient_code: '', reason: '', execute_mint_debit: false });
  const [fundForm, setFundForm] = useState({ email: '', amount: 0 });
  const [fundResult, setFundResult] = useState(null);

  const errDetail = (e) => {
    const d = e.response?.data?.detail;
    return typeof d === 'string' ? d : (d?.error || 'Request failed');
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, tx, recs, fxr] = await Promise.all([
        api.get('/paystack/config'), api.get('/paystack/transactions'),
        api.get('/paystack/recipients'), api.get('/paystack/fx-estimate?usd=100'),
      ]);
      setConfig(cfg.data);
      setTxns(tx.data || []);
      setRecipients(recs.data || []);
      setFx(fxr.data);
      api.get('/paystack/kcb-deliveries').then((r) => setKcb(r.data)).catch(() => {});
    } catch (e) {
      toast.error('Failed to load Paystack status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    api.get('/paystack/banks?currency=KES').then((r) => setBanks(r.data.banks || [])).catch(() => {});
  }, []);

  const createRecipient = async () => {
    if (!recForm.name.trim() || !recForm.account_number.trim() || !recForm.bank_code) {
      toast.error('Fill name, account/phone number and provider'); return;
    }
    setBusy('recipient');
    try {
      const res = await api.post('/paystack/recipient', recForm);
      if (res.data.status === 'ok') {
        toast.success(`Recipient created — ${res.data.recipient_code}`);
        setSettleForm((p) => ({ ...p, recipient_code: res.data.recipient_code }));
        loadAll();
      } else toast.warning(`Paystack returned: ${res.data.status}`);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const doSettle = async () => {
    if (!settleForm.recipient_code) { toast.error('Select or create a recipient first'); return; }
    if (settleForm.execute_mint_debit && !window.confirm(
      `LIVE MONEY: This will debit USD ${settleForm.usd_amount} from the USD Mint (Cyclos) AND send the KES equivalent from the Paystack balance. Proceed?`)) return;
    setBusy('settle');
    try {
      const res = await api.post('/paystack/settle', {
        ...settleForm,
        confirm: settleForm.execute_mint_debit ? 'EXECUTE-LIVE' : '',
      });
      const s = res.data.status;
      if (s === 'transferred' || s === 'processing') toast.success(`Transfer ${s} — ${res.data.reference}`);
      else if (s === 'blocked_insufficient_balance') toast.warning(`Blocked: Paystack balance KES ${res.data.available_kes} < required KES ${res.data.required_kes}. No Mint debit executed.`);
      else if (s === 'otp_required') toast.info('Paystack requires OTP — check the merchant phone, then finalize');
      else toast.error(`Result: ${s}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const initFunding = async () => {
    if (!fundForm.email.trim() || fundForm.amount <= 0) { toast.error('Email and amount required'); return; }
    setBusy('fund');
    try {
      const res = await api.post('/paystack/fund/initialize', fundForm);
      setFundResult(res.data);
      if (res.data.authorization_url) toast.success('Funding checkout created');
      else toast.warning(`Status: ${res.data.status}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const refreshStatus = async (id) => {
    setBusy(`st-${id}`);
    try {
      const res = await api.post(`/paystack/status-update/${id}`);
      toast.info(`Status: ${res.data.current_status}${res.data.paystack_status ? ` (Paystack: ${res.data.paystack_status})` : ''}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const downloadDtr = async (id, reference) => {
    try {
      const res = await api.get(`/paystack/dtr/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `EzyTransfer_DTR_PAYSTACK_${reference || id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed to download DTR'); }
  };

  const health = config?.health;
  const balances = health?.balances || [];
  const kesBalance = balances.find((b) => b.currency === 'KES')?.balance ?? 0;
  const kesRequired = fx?.status === 'ok' ? (settleForm.usd_amount * fx.rate) : null;

  return (
    <div className="space-y-6" data-testid="paystack-layer">
      {/* Connection & balance */}
      <Card className="p-6" data-testid="paystack-connection-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-teal-500 rounded-full" />
            <h3 className="text-xl font-semibold">Paystack — Connection</h3>
            {healthBadge(health?.status)}
            <Badge className="bg-teal-50 text-teal-700 border border-teal-200">KENYA · KES</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={loadAll} data-testid="paystack-refresh-btn" disabled={loading}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="ml-1">Refresh</span>
            </Button>
            <a href="https://dashboard.paystack.com" target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Dashboard</Button>
            </a>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Paystack balance</p>
            {balances.length ? balances.map((b) => (
              <p key={b.currency} className="font-mono text-lg font-bold text-slate-800" data-testid="paystack-balance">
                {b.currency} {Number(b.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            )) : <p className="font-mono text-slate-500">—</p>}
          </div>
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Merchant ID</p>
            <p className="font-mono text-slate-800">{health?.merchant_id || config?.config?.merchant_id || '—'}</p>
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-2">Mode</p>
            <p className="font-mono text-slate-800 uppercase">{config?.config?.mode || '—'}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">FX (USD → KES)</p>
            <p className="font-mono text-slate-800" data-testid="paystack-fx-rate">{fx?.status === 'ok' ? `1 USD = ${fx.rate.toFixed(2)} KES` : '—'}</p>
            <p className="text-[10px] text-slate-400 mt-1">$100 ≈ KES {fx?.status === 'ok' ? fx.kes.toLocaleString() : '—'}</p>
          </div>
        </div>
        {kesBalance <= 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 mt-3" data-testid="paystack-balance-warning">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> Paystack balance is KES 0 — transfers (payouts) send FROM this balance.
              Fund it first via a collection (Fund card below, settled to balance) or a Paystack top-up, otherwise any settlement is blocked before the Mint debit.
            </p>
          </div>
        )}
      </Card>

      {/* KCB → BARWAAKO Delivery Panel */}
      <Card className="p-6 border-indigo-200" data-testid="paystack-kcb-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Banknote className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold">KCB Delivery Panel — Mint ledger vs {kcb?.paystack?.account_name || 'BARWAAKO'} (Paystack)</h3>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 font-mono" data-testid="kcb-mint-balance">
              KCB Mint {kcb?.kcb_mint?.account_number || ''}: {kcb?.kcb_mint?.balance != null ? `$ ${Number(kcb.kcb_mint.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
            </Badge>
            <Badge className="bg-teal-50 text-teal-700 border border-teal-200 font-mono" data-testid="barwaako-balance">
              {kcb?.paystack?.account_name || 'BARWAAKO'} Paystack: KES {kcb?.paystack?.balance_kes != null ? Number(kcb.paystack.balance_kes).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
            </Badge>
          </div>
        </div>
        {!kcb ? (
          <p className="text-sm text-slate-500" data-testid="paystack-kcb-loading">Loading Mint ledger…</p>
        ) : (kcb.rows || []).length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="paystack-kcb-empty">No KCB ledger credits or Paystack legs yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="paystack-kcb-table">
              <thead className="bg-indigo-50/60 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-2 py-1.5" colSpan={4}>USD Mint ledger (KCB partner acct {kcb?.kcb_mint?.account_number})</th>
                  <th className="text-left px-2 py-1.5 border-l border-indigo-100" colSpan={3}>Paystack leg ({kcb?.paystack?.account_name})</th>
                </tr>
                <tr className="border-t border-indigo-100">
                  <th className="text-left px-2 py-1.5">Date</th>
                  <th className="text-left px-2 py-1.5">Mint tx#</th>
                  <th className="text-right px-2 py-1.5">USD</th>
                  <th className="text-left px-2 py-1.5">From</th>
                  <th className="text-left px-2 py-1.5 border-l border-indigo-100">Reference</th>
                  <th className="text-right px-2 py-1.5">KES</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {kcb.rows.map((r, i) => (
                  <tr key={r.mint?.mint_tx || r.paystack?.reference || i} className="border-t border-slate-100 hover:bg-slate-50/50"
                    data-testid={`paystack-kcb-row-${r.mint?.mint_tx || r.paystack?.reference || i}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.mint?.date ? String(r.mint.date).slice(0, 10) : '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.mint?.mint_tx || '—'}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.mint?.amount != null ? `$ ${Number(r.mint.amount).toFixed(2)}` : '—'}</td>
                    <td className="px-2 py-1.5 text-[10px]">{r.mint?.from || '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500 border-l border-indigo-50">{r.paystack?.reference || '—'}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.paystack?.kes_amount != null ? Number(r.paystack.kes_amount).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}</td>
                    <td className="px-2 py-1.5">{r.paystack ? statusChip(r.paystack.status) : <span className="text-[10px] text-slate-400">ledger only</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-2">
          Left side reads LIVE from the USD Mint ledger (KCB partner account). Right side shows the matching Paystack leg on the {kcb?.paystack?.account_name || 'BARWAAKO'} account. Physical KES delivery requires the Paystack balance to be funded.
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recipient */}
        <Card className="p-6" data-testid="paystack-recipient-card">
          <div className="flex items-center gap-3 mb-4">
            <UserPlus className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold">Transfer Recipient</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={recForm.rtype} onValueChange={(v) => setRecForm((p) => ({ ...p, rtype: v }))}>
                <SelectTrigger data-testid="paystack-rec-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mobile_money">Mobile money (M-Pesa)</SelectItem>
                  <SelectItem value="kepss">Bank account (KEPSS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Provider / Bank</Label>
              <Select value={recForm.bank_code} onValueChange={(v) => setRecForm((p) => ({ ...p, bank_code: v }))}>
                <SelectTrigger data-testid="paystack-rec-bank-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {banks.filter((b) => (recForm.rtype === 'mobile_money' ? b.type === 'mobile_money' : b.type !== 'mobile_money')).map((b) => (
                    <SelectItem key={`${b.code}-${b.name}`} value={b.code}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{recForm.rtype === 'mobile_money' ? 'Phone (2547XXXXXXXX)' : 'Account number'}</Label>
              <Input data-testid="paystack-rec-account-input" value={recForm.account_number}
                onChange={(e) => setRecForm((p) => ({ ...p, account_number: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Recipient name</Label>
              <Input data-testid="paystack-rec-name-input" value={recForm.name}
                onChange={(e) => setRecForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
          </div>
          <Button className="mt-4" data-testid="paystack-rec-btn" onClick={createRecipient} disabled={busy === 'recipient'}>
            {busy === 'recipient' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />}
            Create Recipient
          </Button>
          {recipients.length > 0 && (
            <div className="mt-4 text-xs space-y-1" data-testid="paystack-recipients-list">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Saved recipients</p>
              {recipients.map((r) => (
                <p key={r.recipient_code} className="font-mono text-slate-700">
                  {r.recipient_code} · {r.name} · {r.bank_name || r.bank_code} · {r.account_number}
                </p>
              ))}
            </div>
          )}
        </Card>

        {/* Settle */}
        <Card className="p-6 border-teal-200" data-testid="paystack-settle-card">
          <div className="flex items-center gap-3 mb-4">
            <Send className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold">Send Settlement (USD → KES)</h3>
            <Badge className="bg-rose-50 text-rose-700 border border-rose-200">LIVE</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>USD amount</Label>
              <Input data-testid="paystack-settle-usd-input" type="number" value={settleForm.usd_amount}
                onChange={(e) => setSettleForm((p) => ({ ...p, usd_amount: parseFloat(e.target.value) || 0 }))} />
              {kesRequired !== null && (
                <p className="text-[10px] text-slate-500 font-mono">≈ KES {kesRequired.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Recipient</Label>
              <Select value={settleForm.recipient_code} onValueChange={(v) => setSettleForm((p) => ({ ...p, recipient_code: v }))}>
                <SelectTrigger data-testid="paystack-settle-recipient-select"><SelectValue placeholder="Select recipient" /></SelectTrigger>
                <SelectContent>
                  {recipients.map((r) => (
                    <SelectItem key={r.recipient_code} value={r.recipient_code}>{r.name} · {r.account_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Reason</Label>
              <Input data-testid="paystack-settle-reason-input" value={settleForm.reason}
                onChange={(e) => setSettleForm((p) => ({ ...p, reason: e.target.value }))} />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3" data-testid="paystack-mint-debit-toggle">
            <Checkbox id="ps-mint" checked={settleForm.execute_mint_debit}
              onCheckedChange={(v) => setSettleForm((p) => ({ ...p, execute_mint_debit: !!v }))} />
            <label htmlFor="ps-mint" className="text-xs text-slate-700">
              Also debit <span className="font-mono">USD {settleForm.usd_amount}</span> from USD Mint (Cyclos) — REAL money
            </label>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">
            Safety order: FX rate → Paystack balance check → (Mint debit) → transfer. If the balance can't cover the KES amount, everything aborts BEFORE any debit.
          </p>
          <Button className="mt-3 bg-teal-600 hover:bg-teal-700" data-testid="paystack-settle-btn" onClick={doSettle} disabled={busy === 'settle'}>
            {busy === 'settle' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send Settlement
          </Button>
        </Card>

        {/* Fund */}
        <Card className="p-6" data-testid="paystack-fund-card">
          <div className="flex items-center gap-3 mb-4">
            <Wallet className="w-5 h-5 text-teal-600" />
            <h3 className="text-lg font-semibold">Fund Balance (Collection Checkout)</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Payer email</Label>
              <Input data-testid="paystack-fund-email-input" value={fundForm.email}
                onChange={(e) => setFundForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Amount (KES)</Label>
              <Input data-testid="paystack-fund-amount-input" type="number" value={fundForm.amount}
                onChange={(e) => setFundForm((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
            </div>
          </div>
          <Button className="mt-4" variant="outline" data-testid="paystack-fund-btn" onClick={initFunding} disabled={busy === 'fund'}>
            {busy === 'fund' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Banknote className="w-4 h-4 mr-1" />}
            Create Checkout Link
          </Button>
          {fundResult?.authorization_url && (
            <div className="mt-3 text-xs border rounded-md p-3 bg-slate-50" data-testid="paystack-fund-result">
              <p className="text-slate-500 mb-1">Share this link with the payer (M-Pesa / card):</p>
              <a href={fundResult.authorization_url} target="_blank" rel="noreferrer" className="font-mono text-teal-700 underline break-all">
                {fundResult.authorization_url}
              </a>
            </div>
          )}
          <p className="text-[10px] text-slate-500 mt-2">
            Note: collections settle per your Paystack settlement schedule. To use them for transfers, set settlement to "Balance" in the Paystack dashboard (Settings → Preferences).
          </p>
        </Card>
      </div>

      {/* Transactions */}
      <Card className="p-6" data-testid="paystack-transactions-card">
        <h3 className="text-xl font-semibold mb-4">Paystack Transactions</h3>
        {txns.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="paystack-transactions-empty">No Paystack transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="paystack-transactions-table">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-2 py-1.5">Date (UTC)</th>
                  <th className="text-left px-2 py-1.5">Reference</th>
                  <th className="text-left px-2 py-1.5">Type</th>
                  <th className="text-right px-2 py-1.5">USD</th>
                  <th className="text-right px-2 py-1.5">KES</th>
                  <th className="text-left px-2 py-1.5">Mint tx</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-center px-2 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-2 py-1.5 font-mono text-[10px]">{(t.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{t.reference}</td>
                    <td className="px-2 py-1.5">{(t.type || '').replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{t.usd_amount ? `$ ${Number(t.usd_amount).toFixed(2)}` : '-'}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{t.amount ? Number(t.amount).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{t.mint_transaction_number || '-'}</td>
                    <td className="px-2 py-1.5">{statusChip(t.status)}{t.paystack_status ? <span className="ml-1 text-[9px] text-slate-400 font-mono">{t.paystack_status}</span> : null}</td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">
                      {t.type === 'fund_charge' && t.authorization_url && (t.status === 'initialized' || t.status === 'pending') && (
                        <a href={t.authorization_url} target="_blank" rel="noreferrer" data-testid={`paystack-paylink-${t.id}`}
                          className="text-[10px] underline text-emerald-700 hover:text-emerald-900 mr-2">
                          Pay link
                        </a>
                      )}
                      <button onClick={() => refreshStatus(t.id)} data-testid={`paystack-status-${t.id}`}
                        className="text-[10px] underline text-teal-700 hover:text-teal-900 mr-2" disabled={busy === `st-${t.id}`}>
                        {busy === `st-${t.id}` ? '…' : 'Refresh'}
                      </button>
                      <button onClick={() => downloadDtr(t.id, t.reference)} data-testid={`paystack-dtr-${t.id}`}
                        className="text-[10px] underline text-indigo-700 hover:text-indigo-900">
                        DTR
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PaystackLayer;
